import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface StepperStep {
  label: string;
  description?: string;
  optional?: boolean;
}

export function Stepper({
  steps,
  current,
  errorIndex,
  compact = false,
  className,
}: {
  steps: StepperStep[];
  current: number;
  errorIndex?: number;
  compact?: boolean;
  className?: string;
}) {
  return (
    <ol aria-label="Progress steps" className={cn("flex flex-wrap items-start", className)}>
      {steps.map((s, i) => {
        const state =
          errorIndex === i ? "error" : i < current ? "complete" : i === current ? "current" : "upcoming";
        const isLast = i === steps.length - 1;
        return (
          <li
            key={s.label}
            aria-current={state === "current" ? "step" : undefined}
            className={cn("flex items-start", !compact && "min-w-[150px] flex-1")}
          >
            <div className="flex shrink-0 items-start gap-2 pr-2">
              <span
                aria-hidden
                className={cn(
                  "grid size-6 shrink-0 place-items-center rounded-full border text-[11px] font-bold",
                  state === "complete" && "border-success bg-success text-white",
                  state === "current" && "border-primary bg-primary text-white",
                  state === "error" && "border-danger bg-danger text-white",
                  state === "upcoming" && "border-line bg-surface text-muted"
                )}
              >
                {state === "complete" ? (
                  <Check aria-hidden className="size-3.5" />
                ) : state === "error" ? (
                  <X aria-hidden className="size-3" />
                ) : (
                  i + 1
                )}
              </span>
              <div className="leading-tight">
                <div
                  className={cn(
                    "text-xs font-semibold",
                    state === "upcoming" ? "text-muted" : "text-ink",
                    state === "error" && "text-danger"
                  )}
                >
                  {s.label}
                </div>
                {!compact && s.description && <div className="text-[11px] text-muted">{s.description}</div>}
                {s.optional && <div className="text-[11px] text-subtle">Optional</div>}
              </div>
            </div>
            {!isLast && !compact && (
              <div aria-hidden className={cn("mt-3 h-px min-w-6 flex-1 bg-line")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}